import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Form, Button, Card, Row, Col, Image, Spinner, Alert, InputGroup, Accordion, Tab, Nav } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import { getSettings, updateSettings, uploadLogo, uploadQrCode, checkSlugAvailability } from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import { handlePhoneBlurLogic } from '../utils/phoneUtils';
import ReceiptView from '../components/ReceiptView';
import ExpenseReceiptPreview from '../components/ExpenseReceiptPreview';
import SettlementReceiptPreview from '../components/SettlementReceiptPreview';
import CashRegisterStationsManager from '../components/CashRegisterStationsManager';
import ExpenseCategoriesManager from '../components/ExpenseCategoriesManager';
import BusinessSetupGuide from '../components/BusinessSetupGuide';
import { isSeen } from '../utils/onboardingSeen';

const ReadOnlyInput = ({ value, ...rest }) => (
    <Form.Control {...rest} value={value} readOnly />
);

const slugify = (value) =>
    (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const BusinessSettingsPage = () => {
    const { user, business, setBusiness, setUser } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [publicRegistrationLink, setPublicRegistrationLink] = useState('');
    const [settings, setSettings] = useState({
        address: '',
        phone_country_code: '',
        phone: '',
        contact_email: '',
        logo_url: '',
        social_media: {
            facebook: '',
            instagram: '',
            twitter: '',
            linkedin: '',
            tiktok: '',
            whatsapp: '',
        },
        previous_invoice_number: 0,
        invoice_color: '#FFFFFF',
        invoice_footer: '¡Gracias por tu compra! 🤝',
        language: 'es',
        fiscal_id_type: '',
        fiscal_id: '',
        qr_code_url: '',
        // Expense receipt options
        expense_invoice_color: '#FFFFFF',
        expense_invoice_footer: '¡Gracias por su servicio! 🤝',
        expense_invoice_display_options: {
            show_supplier_info: true,
            show_expense_details: true,
            show_payment_method: true,
            show_observations: true,
            show_qr_code: true
        },
        // Settlement receipt options
        settlement_invoice_color: '#FFFFFF',
        settlement_invoice_footer: 'Gracias por su colaboración.',
        settlement_invoice_display_options: {
            show_supplier_info: true,
            show_deductions: true,
            show_payment_info: true,
            show_notes: true,
            show_qr_code: true
        }
    });
    const [businessName, setBusinessName] = useState('');
    const [businessSlug, setBusinessSlug] = useState('');
    const [slugAvailable, setSlugAvailable] = useState(null);
    const [checkingSlug, setCheckingSlug] = useState(false);
    const [slugTouched, setSlugTouched] = useState(false);
    const [slugCustomized, setSlugCustomized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [qrCodeFile, setQrCodeFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await getSettings();
                if (response.data) {
                    setSettings(prev => ({
                        ...prev,
                        ...response.data,
                        social_media: { ...prev.social_media, ...response.data.social_media },
                        invoice_color: response.data.invoice_color || '#FFFFFF',
                        invoice_footer: response.data.invoice_footer || '¡Gracias por tu compra! 🤝',
                        language: response.data.language || 'es',
                        expense_invoice_color: response.data.expense_invoice_color || response.data.invoice_color || '#FFFFFF',
                        expense_invoice_footer: response.data.expense_invoice_footer || '¡Gracias por su servicio! 🤝',
                        expense_invoice_display_options: response.data.expense_invoice_display_options || {
                            show_supplier_info: true,
                            show_expense_details: true,
                            show_payment_method: true,
                            show_observations: true,
                            show_qr_code: true
                        },
                        settlement_invoice_color: response.data.settlement_invoice_color || response.data.invoice_color || '#FFFFFF',
                        settlement_invoice_footer: response.data.settlement_invoice_footer || 'Gracias por su colaboración.',
                        settlement_invoice_display_options: response.data.settlement_invoice_display_options || {
                            show_supplier_info: true,
                            show_deductions: true,
                            show_payment_info: true,
                            show_notes: true,
                            show_qr_code: true
                        }
                    }));
                }
            } catch (error) {
                console.error("Failed to fetch settings", error);
                const errorMsg = t('businessSettingsPage.toasts.loadFailed');
                setError(errorMsg);
                toast.error(errorMsg);
            } finally {
                setLoading(false);
            }
        };
        if (user && hasPermission(PERMISSIONS.VIEW_BUSINESS_SETTINGS)) {
            fetchSettings();
        } else {
            setLoading(false);
            setError(t('businessSettingsPage.toasts.permissionError'));
        }
    }, [user, hasPermission, t]);

    useEffect(() => {
        if (business && business.name) {
            setBusinessName(business.name);
        }
        if (business && business.slug) {
            setBusinessSlug(business.slug);
            setSlugCustomized(business.slug !== slugify(business.name));
            setPublicRegistrationLink(`${window.location.origin}/businesses/${business.slug}/register-client`);
        }
    }, [business]);

    // Auto-generate slug from business name unless the user has customized it
    useEffect(() => {
        if (businessName && !slugCustomized) {
            setBusinessSlug(slugify(businessName));
        }
    }, [businessName, slugCustomized]);

    // Validate slug availability with debounce
    const validateSlug = useCallback(async (slug) => {
        if (!slug || slug.trim() === '') {
            setSlugAvailable(null);
            return;
        }

        setCheckingSlug(true);
        try {
            const response = await checkSlugAvailability(slug);
            setSlugAvailable(response.data.available);
        } catch (error) {
            console.error('Error checking slug availability:', error);
            setSlugAvailable(null);
        } finally {
            setCheckingSlug(false);
        }
    }, []);

    // Debounce slug validation
    useEffect(() => {
        if (slugTouched && businessSlug) {
            const timer = setTimeout(() => {
                validateSlug(businessSlug);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [businessSlug, slugTouched, validateSlug]);

    // Al llegar desde el banner ("Datos del Negocio"), revela el botón de guardar con un
    // scroll ligero — pero solo si BusinessSetupGuide.js no va a correr (si va a correr, es
    // ella quien guía el scroll paso a paso hasta terminar ahí mismo; hacer ambas cosas a la
    // vez compite, porque la guía arranca arriba, en el logo).
    useEffect(() => {
        if (loading || !location.state?.scrollToSave) return;
        const guideWillRun = user?.role === 'owner'
            && user?.is_setup_complete === false
            && !isSeen(user.id, 'business_setup_guide');
        if (guideWillRun) return;
        document.getElementById('onboarding-save-business')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(publicRegistrationLink)
            .then(() => toast.success(t('toast.linkCopied')))
            .catch((err) => toast.error(t('toast.copyFailed')));
    };

    const handleSlugChange = (e) => {
        // Public URL segment: force lowercase and turn spaces / invalid chars into
        // hyphens as the user types. Leading/trailing hyphens are cleaned on blur.
        const newSlug = e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-');
        setBusinessSlug(newSlug);
        setSlugTouched(true);
        // An empty field resumes auto-suggestions from the name
        setSlugCustomized(newSlug !== '');
    };

    const handleSlugBlur = () => {
        const cleaned = slugify(businessSlug);
        setBusinessSlug(cleaned);
        setSlugCustomized(cleaned !== '');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('social_media.')) {
            const socialPlatform = name.split('.')[1];
            setSettings(prev => ({ ...prev, social_media: { ...prev.social_media, [socialPlatform]: value } }));
        } else {
            setSettings(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleCountryCodeChange = (value) => {
        setSettings(prev => ({ ...prev, phone_country_code: value }));
    };

    const handlePhoneBlur = () => {
        handlePhoneBlurLogic(
            settings.phone,
            settings.phone_country_code,
            (newCountry) => setSettings(prev => ({ ...prev, phone_country_code: newCountry })),
            (newPhone) => setSettings(prev => ({ ...prev, phone: newPhone }))
        );
    };

    const handleFileChange = (e) => {
        setLogoFile(e.target.files[0]);
    };

    const handleQrCodeFileChange = (e) => {
        setQrCodeFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate slug availability before submitting
        if (slugTouched && slugAvailable === false) {
            toast.error('El slug no está disponible. Por favor, elige otro.');
            return;
        }

        setSubmitting(true);
        const toastId = toast.loading(t('businessSettingsPage.toasts.saving'));

        try {
            // Normalize the slug once more in case the form was submitted without blur
            const cleanedSlug = slugify(businessSlug);
            if (cleanedSlug !== businessSlug) {
                setBusinessSlug(cleanedSlug);
            }
            let finalSettings = { ...settings, name: businessName, slug: cleanedSlug };

            if (logoFile) {
                const uploadResponse = await uploadLogo(logoFile);
                finalSettings.logo_url = uploadResponse.data.logo_url;
            }

            if (qrCodeFile) {
                const uploadQrResponse = await uploadQrCode(qrCodeFile);
                finalSettings.qr_code_url = uploadQrResponse.data.qr_code_url;
            }

            await updateSettings(finalSettings);
            setSettings(finalSettings);

            // Update business in context if name, slug, or logo changed
            if (business) {
                const shouldUpdate =
                    businessName !== business.name ||
                    cleanedSlug !== business.slug ||
                    (finalSettings.logo_url && finalSettings.logo_url !== business.logo_url);

                if (shouldUpdate) {
                    setBusiness({
                        ...business,
                        name: businessName,
                        slug: cleanedSlug,
                        logo_url: finalSettings.logo_url || business.logo_url
                    });
                    // Update public registration link
                    setPublicRegistrationLink(`${window.location.origin}/businesses/${cleanedSlug}/register-client`);
                }
            }

            // Update user's is_setup_complete to true if it was false
            if (user && user.is_setup_complete === false) {
                setUser({ ...user, is_setup_complete: true });
            }

            // If the current user doesn't have a specific language set, apply the new business language
            if (!user.language) {
                i18n.changeLanguage(finalSettings.language);
            }

            // Reset slug touched state after successful save; keep "customized"
            // in sync with the saved value so a later name edit won't clobber it
            setSlugTouched(false);
            setSlugCustomized(cleanedSlug !== slugify(businessName));

            toast.success(t('businessSettingsPage.toasts.saved'), { id: toastId });

            // Redirect to plan selection if subscription is pending
            if (business?.subscription_status === 'pending') {
                setTimeout(() => {
                    navigate('/settings/plan');
                }, 1000);
            }
        } catch (error) {
            console.error("Failed to save settings", error);
            const errorMsg = error.response?.data?.message || t('businessSettingsPage.toasts.saveFailed');
            toast.error(errorMsg, { id: toastId });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Container className="mt-4 text-center"><Spinner animation="border" /></Container>;
    }

    if (error) {
        return <Container className="mt-4"><Alert variant="danger">{error}</Alert></Container>;
    }

    if (!hasPermission(PERMISSIONS.VIEW_BUSINESS_SETTINGS)) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{t('businessSettingsPage.toasts.permissionError')}</Alert>
            </Container>
        );
    }

    const canEdit = hasPermission(PERMISSIONS.EDIT_BUSINESS_SETTINGS);

    return (
        <Container className="mt-4">
            <BusinessSetupGuide />
            <Card>
                <Card.Header as="h2">{t('businessSettingsPage.title')}</Card.Header>
                <Card.Body>
                    {user && user.is_setup_complete === false && (
                        <Alert variant="info" className="mb-4">
                            <Alert.Heading>¡Bienvenido/a a Evazard!</Alert.Heading>
                            <p>Por favor, completa la configuración de tu negocio para empezar. Asegúrate de ingresar el nombre de tu negocio.</p>
                        </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                        <fieldset disabled={!canEdit}>
                            <Accordion defaultActiveKey={['0']} alwaysOpen className="mb-4 accordion-separated">
                                {/* Sección 1: Información del Negocio */}
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>{t('businessSettingsPage.sections.businessInfo')}</Accordion.Header>
                                    <Accordion.Body>
                                        <Form.Group id="onboarding-field-logo" className="mb-4 text-center">
                                            <Form.Label><h5>{t('businessSettingsPage.logoTitle')}</h5></Form.Label>
                                            <div>
                                                <Image
                                                    src={settings.logo_url || 'https://via.placeholder.com/150'}
                                                    roundedCircle
                                                    style={{ width: '150px', height: '150px', objectFit: 'cover', border: '4px solid #eee' }}
                                                />
                                            </div>
                                            <Form.Control
                                                type="file"
                                                name="logo"
                                                onChange={handleFileChange}
                                                className="mt-3"
                                                accept="image/png, image/jpeg, image/gif"
                                            />
                                        </Form.Group>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3" controlId="business_name">
                                                    <Form.Label>Nombre del Negocio</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="business_name"
                                                        value={businessName || ''}
                                                        onChange={(e) => setBusinessName(e.target.value)}
                                                        placeholder="Ej: Cafetería La Esquina"
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group id="onboarding-field-slug" className="mb-3" controlId="business_slug">
                                                    <Form.Label>
                                                        Slug del Negocio
                                                        <small className="text-muted ms-2">(URL pública)</small>
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="text"
                                                            name="business_slug"
                                                            value={businessSlug || ''}
                                                            onChange={handleSlugChange}
                                                            onBlur={handleSlugBlur}
                                                            placeholder="ej: cafeteria-la-esquina"
                                                            isInvalid={slugTouched && slugAvailable === false}
                                                            isValid={slugTouched && slugAvailable === true}
                                                        />
                                                        {checkingSlug && (
                                                            <InputGroup.Text>
                                                                <Spinner animation="border" size="sm" />
                                                            </InputGroup.Text>
                                                        )}
                                                        <Form.Control.Feedback type="invalid">
                                                            Este slug ya está en uso
                                                        </Form.Control.Feedback>
                                                        <Form.Control.Feedback type="valid">
                                                            Slug disponible
                                                        </Form.Control.Feedback>
                                                    </InputGroup>
                                                    <Form.Text className="text-muted">
                                                        Se genera automáticamente desde el nombre. Puedes editarlo manualmente.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row id="onboarding-field-fiscal-id">
                                            <Col md={4}>
                                                <Form.Group className="mb-3" controlId="fiscal_id_type">
                                                    <Form.Label>{t('businessSettingsPage.fiscalIdType')}</Form.Label>
                                                    <Form.Select
                                                        name="fiscal_id_type"
                                                        value={settings.fiscal_id_type || ''}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Seleccione...</option>
                                                        <option value="NIT">NIT</option>
                                                        <option value="CC">Cédula de Ciudadanía (CC)</option>
                                                        <option value="RUT">RUT</option>
                                                        <option value="TI">Tarjeta de Identidad (TI)</option>
                                                        <option value="CE">Cédula de Extranjería (CE)</option>
                                                        <option value="PASSPORT">Pasaporte</option>
                                                        <option value="OTHER">Otro</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={8}>
                                                <Form.Group className="mb-3" controlId="fiscal_id">
                                                    <Form.Label>{t('businessSettingsPage.fiscalId')}</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="fiscal_id"
                                                        value={settings.fiscal_id || ''}
                                                        onChange={handleChange}
                                                        placeholder={t('businessSettingsPage.fiscalIdPlaceholder')}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <div id="onboarding-save-business" className="d-flex justify-content-end mt-3">
                                            <Button variant="primary" type="submit" disabled={submitting}>
                                                {submitting ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> {t('businessSettingsPage.savingButton')}</> : "Guardar Cambios"}
                                            </Button>
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* Sección 2: Datos de Contacto */}
                                <Accordion.Item eventKey="1">
                                    <Accordion.Header>{t('businessSettingsPage.sections.contactInfo')}</Accordion.Header>
                                    <Accordion.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3" controlId="contact_email">
                                                    <Form.Label>Correo de Contacto</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="contact_email"
                                                        value={settings.contact_email || ''}
                                                        onChange={handleChange}
                                                        placeholder="contacto@tunegocio.com"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3" controlId="phone">
                                                    <Form.Label>Número de Teléfono</Form.Label>
                                                    <Row>
                                                        <Col xs={5} md={4}>
                                                            <PhoneInput
                                                                value={settings.phone_country_code}
                                                                onChange={handleCountryCodeChange}
                                                                numberInputComponent={ReadOnlyInput}
                                                                className="phone-input-custom"
                                                                defaultCountry="CO"
                                                                international
                                                            />
                                                        </Col>
                                                        <Col xs={7} md={8}>
                                                            <Form.Control
                                                                type="tel"
                                                                name="phone"
                                                                value={settings.phone || ''}
                                                                onChange={handleChange}
                                                                onBlur={handlePhoneBlur}
                                                                placeholder="3001234567"
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-3" controlId="address">
                                            <Form.Label>{t('businessSettingsPage.address')}</Form.Label>
                                            <Form.Control as="textarea" rows={3} name="address" value={settings.address || ''} onChange={handleChange} placeholder={t('businessSettingsPage.addressPlaceholder')} />
                                        </Form.Group>
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* Sección 3: Enlace de Registro Público */}
                                <Accordion.Item eventKey="2">
                                    <Accordion.Header>{t('businessSettingsPage.sections.publicRegistration')}</Accordion.Header>
                                    <Accordion.Body>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('businessSettingsPage.publicClientRegistrationLink.label')}</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={publicRegistrationLink}
                                                />
                                                <Button variant="outline-secondary" onClick={handleCopyLink}>
                                                    {t('businessSettingsPage.publicClientRegistrationLink.copyButton')}
                                                </Button>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                {t('businessSettingsPage.publicClientRegistrationLink.instructions')}
                                            </Form.Text>
                                        </Form.Group>
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* Sección 4: Preferencias */}
                                <Accordion.Item eventKey="3">
                                    <Accordion.Header>{t('businessSettingsPage.sections.preferences')}</Accordion.Header>
                                    <Accordion.Body>
                                        <Row className="mb-4">
                                            <Col md={6}>
                                                <Form.Group controlId="language">
                                                    <Form.Label>{t('businessSettingsPage.language.label')}</Form.Label>
                                                    <Form.Select name="language" value={settings.language || 'en'} onChange={handleChange}>
                                                        <option value="en">{t('businessSettingsPage.language.english')}</option>
                                                        <option value="es">{t('businessSettingsPage.language.spanish')}</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* Sección 5: Personalización de Recibo */}
                                <Accordion.Item eventKey="4">
                                    <Accordion.Header>{t('businessSettingsPage.sections.receiptCustomization')}</Accordion.Header>
                                    <Accordion.Body>
                                        <Tab.Container defaultActiveKey="salesReceipt">
                                            <Nav variant="tabs" className="mb-4">
                                                <Nav.Item>
                                                    <Nav.Link eventKey="salesReceipt">{t('businessSettingsPage.tabs.salesReceipt')}</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link eventKey="expenseReceipt">{t('businessSettingsPage.tabs.expenseReceipt')}</Nav.Link>
                                                </Nav.Item>
                                                {business?.plan_features?.supplier_settlements && (
                                                    <Nav.Item>
                                                        <Nav.Link eventKey="settlementReceipt">{t('businessSettingsPage.tabs.settlementReceipt')}</Nav.Link>
                                                    </Nav.Item>
                                                )}
                                            </Nav>
                                            <Tab.Content>
                                                {/* Tab: Recibo de Ventas */}
                                                <Tab.Pane eventKey="salesReceipt">
                                                    <Row>
                                                        {/* Left Column: Controls */}
                                                        <Col md={5}>
                                                            {/* Logo Input */}
                                                            <Form.Group className="mb-4" controlId="invoice_logo_secondary">
                                                                <Form.Label>Logo del Negocio</Form.Label>
                                                                <div className="d-flex align-items-center">
                                                                    <Form.Control
                                                                        type="file"
                                                                        onChange={handleFileChange}
                                                                        accept="image/*"
                                                                        size="sm"
                                                                    />
                                                                    {settings.logo_url && !logoFile && (
                                                                        <span className="ms-2 text-success" title="Logo actual guardado">✓</span>
                                                                    )}
                                                                </div>
                                                                <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                    Se mostrará en la parte superior del recibo.
                                                                </Form.Text>
                                                            </Form.Group>

                                                            {/* QR Code Input */}
                                                            <Form.Group className="mb-4" controlId="invoice_qr_code">
                                                                <Form.Label>{t('businessSettingsPage.qrCodeTitle')}</Form.Label>
                                                                <div className="d-flex align-items-center flex-column align-items-start">
                                                                    {settings.qr_code_url && (
                                                                        <Image
                                                                            src={settings.qr_code_url}
                                                                            thumbnail
                                                                            style={{ width: '100px', height: '100px', objectFit: 'contain', marginBottom: '10px' }}
                                                                        />
                                                                    )}
                                                                    <Form.Control
                                                                        type="file"
                                                                        onChange={handleQrCodeFileChange}
                                                                        accept="image/*"
                                                                        size="sm"
                                                                    />
                                                                    {settings.qr_code_url && !qrCodeFile && (
                                                                        <span className="mt-1 text-success" style={{ fontSize: '0.8rem' }}>✓ QR actual guardado</span>
                                                                    )}
                                                                </div>
                                                                <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                    {t('businessSettingsPage.qrCodeHelp')}
                                                                </Form.Text>
                                                            </Form.Group>

                                                            {/* Color Picker */}
                                                            <Form.Group className="mb-4" controlId="invoice_color">
                                                                <Form.Label>{t('businessSettingsPage.invoiceTheme')}</Form.Label>
                                                                <Form.Control
                                                                    type="color"
                                                                    name="invoice_color"
                                                                    value={settings.invoice_color || '#FFFFFF'}
                                                                    onChange={handleChange}
                                                                    title={t('businessSettingsPage.invoiceThemeTooltip')}
                                                                    list="presetColors"
                                                                    className="w-100"
                                                                    style={{ height: '40px' }}
                                                                />
                                                                <datalist id="presetColors">
                                                                    <option>#FFFFFF</option>
                                                                    <option>#F0F8FF</option>
                                                                    <option>#FAEBD7</option>
                                                                    <option>#F5F5DC</option>
                                                                    <option>#FFE4C4</option>
                                                                    <option>#FFF8DC</option>
                                                                    <option>#E0FFFF</option>
                                                                    <option>#FAFAD2</option>
                                                                    <option>#D3D3D3</option>
                                                                    <option>#90EE90</option>
                                                                    <option>#FFB6C1</option>
                                                                    <option>#FFA07A</option>
                                                                    <option>#20B2AA</option>
                                                                    <option>#87CEFA</option>
                                                                    <option>#778899</option>
                                                                    <option>#B0C4DE</option>
                                                                    <option>#FFFFE0</option>
                                                                    <option>#98FB98</option>
                                                                    <option>#AFEEEE</option>
                                                                    <option>#D8BFD8</option>
                                                                    <option>#F5DEB3</option>
                                                                    <option>#F0FFFF</option>
                                                                    <option>#F5F5F5</option>
                                                                </datalist>
                                                            </Form.Group>

                                                            {/* Footer Text */}
                                                            <Form.Group className="mb-4" controlId="invoice_footer">
                                                                <Form.Label>{t('businessSettingsPage.invoiceFooter')}</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={3}
                                                                    name="invoice_footer"
                                                                    value={settings.invoice_footer || ''}
                                                                    onChange={handleChange}
                                                                    placeholder={t('businessSettingsPage.invoiceFooterPlaceholder')}
                                                                />
                                                            </Form.Group>

                                                            {/* Visible Sections Toggles */}
                                                            <Card className="mb-4 bg-light border-0">
                                                                <Card.Body>
                                                                    <h6 className="card-title mb-3">Secciones Visibles del Recibo</h6>
                                                                    <Form.Check className="mb-2" type="switch" id="show_client_info" label="Información del Cliente"
                                                                        checked={settings.invoice_display_options?.show_client_info !== false}
                                                                        onChange={(e) => setSettings({ ...settings, invoice_display_options: { ...settings.invoice_display_options, show_client_info: e.target.checked } })}
                                                                    />
                                                                    <Form.Check className="mb-2" type="switch" id="show_delivery_date" label="Fecha de Entrega"
                                                                        checked={settings.invoice_display_options?.show_delivery_date !== false}
                                                                        onChange={(e) => setSettings({ ...settings, invoice_display_options: { ...settings.invoice_display_options, show_delivery_date: e.target.checked } })}
                                                                    />
                                                                    <Form.Check className="mb-2" type="switch" id="show_payment_history" label="Historial de Pagos"
                                                                        checked={settings.invoice_display_options?.show_payment_history !== false}
                                                                        onChange={(e) => setSettings({ ...settings, invoice_display_options: { ...settings.invoice_display_options, show_payment_history: e.target.checked } })}
                                                                    />
                                                                    <Form.Check className="mb-0" type="switch" id="show_observations" label="Notas / Observaciones"
                                                                        checked={settings.invoice_display_options?.show_observations !== false}
                                                                        onChange={(e) => setSettings({ ...settings, invoice_display_options: { ...settings.invoice_display_options, show_observations: e.target.checked } })}
                                                                    />
                                                                    <Form.Check className="mb-0" type="switch" id="show_qr_code" label="Mostrar Código QR"
                                                                        checked={settings.invoice_display_options?.show_qr_code !== false}
                                                                        onChange={(e) => setSettings({ ...settings, invoice_display_options: { ...settings.invoice_display_options, show_qr_code: e.target.checked } })}
                                                                    />
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>

                                                        {/* Right Column: Preview */}
                                                        <Col md={7}>
                                                            <div className="d-flex flex-column align-items-center">
                                                                <h5 className="mb-3 text-muted">Vista Previa del Recibo</h5>
                                                                <div className="border p-3 bg-light w-100 d-flex justify-content-center" style={{ minHeight: '500px', borderRadius: '8px' }}>
                                                                    <div style={{ transform: 'scale(0.85)', transformOrigin: 'top center', width: '100%', maxWidth: '400px' }}>
                                                                        <ReceiptView
                                                                            previewMode={true}
                                                                            sale={{
                                                                                invoice_number: '001',
                                                                                logo_url: logoFile ? URL.createObjectURL(logoFile) : (settings.logo_url || ''),
                                                                                business_name: businessName || 'Nombre del Negocio',
                                                                                business_address: settings.address || 'Calle Principal #123',
                                                                                business_phone: `${settings.phone_country_code || ''} ${settings.phone || ''}`.trim() || '+57 300 123 4567',
                                                                                fiscal_id_type: settings.fiscal_id_type,
                                                                                fiscal_id: settings.fiscal_id,
                                                                                qr_code_url: qrCodeFile ? URL.createObjectURL(qrCodeFile) : (settings.qr_code_url || ''),
                                                                                client_name: 'María González',
                                                                                client_phone: '+57 310 987 6543',
                                                                                client_address: 'Av. Siempre Viva 742, Apt 4B',
                                                                                delivery_datetime: new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString(),
                                                                                products: [
                                                                                    { item_id: 1, quantity: 1, product_name: 'Hamburguesa Especial', unit_price: 25000 },
                                                                                    { item_id: 2, quantity: 2, product_name: 'Coca Cola 400ml', unit_price: 5000 },
                                                                                    { item_id: 3, quantity: 1, product_name: 'Papas Fritas Medianas', unit_price: 8000 }
                                                                                ],
                                                                                total_amount: 43000,
                                                                                paid_amount: 20000,
                                                                                payments: [{ payment_id: 1, payment_date: new Date().toISOString(), payment_method: 'cash', amount: 20000 }],
                                                                                observations: 'Sin cebolla en la hamburguesa. Entregar en portería.',
                                                                                created_at: new Date().toISOString(),
                                                                                invoice_color: settings.invoice_color || '#0B3D91',
                                                                                invoice_display_options: settings.invoice_display_options,
                                                                                invoice_footer: settings.invoice_footer
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Tab.Pane>

                                                {/* Tab: Recibo de Gastos */}
                                                <Tab.Pane eventKey="expenseReceipt">
                                                    <Row>
                                                        <Col md={6}>
                                                            <Alert variant="info" className="mb-4">
                                                                <small>Configura la apariencia del recibo de gastos que se genera en <code>/expense-receipt/&lt;id&gt;</code></small>
                                                            </Alert>

                                                            {/* Color Picker for Expense */}
                                                            <Form.Group className="mb-4" controlId="expense_invoice_color">
                                                                <Form.Label>Color de Tema para Recibo de Gastos</Form.Label>
                                                                <Form.Control
                                                                    type="color"
                                                                    name="expense_invoice_color"
                                                                    value={settings.expense_invoice_color || '#FFFFFF'}
                                                                    onChange={handleChange}
                                                                    title="Selecciona un color de fondo para el recibo de gastos"
                                                                    list="presetColorsExpense"
                                                                    className="w-100"
                                                                    style={{ height: '40px' }}
                                                                />
                                                                <datalist id="presetColorsExpense">
                                                                    <option>#FFFFFF</option>
                                                                    <option>#F0F8FF</option>
                                                                    <option>#FAEBD7</option>
                                                                    <option>#F5F5DC</option>
                                                                    <option>#E0FFFF</option>
                                                                    <option>#FAFAD2</option>
                                                                    <option>#D3D3D3</option>
                                                                    <option>#FFB6C1</option>
                                                                    <option>#87CEFA</option>
                                                                    <option>#F5F5F5</option>
                                                                </datalist>
                                                            </Form.Group>

                                                            {/* Footer Text for Expense */}
                                                            <Form.Group className="mb-4" controlId="expense_invoice_footer">
                                                                <Form.Label>Texto de Pie de Página</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={3}
                                                                    name="expense_invoice_footer"
                                                                    value={settings.expense_invoice_footer || ''}
                                                                    onChange={handleChange}
                                                                    placeholder="Ej: ¡Gracias por su servicio!"
                                                                />
                                                            </Form.Group>

                                                            {/* Visible Sections Toggles for Expense */}
                                                            <Card className="mb-4 bg-light border-0">
                                                                <Card.Body>
                                                                    <h6 className="card-title mb-3">Secciones Visibles del Recibo de Gastos</h6>
                                                                    <Form.Check className="mb-2" type="switch" id="expense_show_supplier_info" label="Información del Proveedor"
                                                                        checked={settings.expense_invoice_display_options?.show_supplier_info !== false}
                                                                        onChange={(e) => setSettings({ ...settings, expense_invoice_display_options: { ...settings.expense_invoice_display_options, show_supplier_info: e.target.checked } })}
                                                                    />
                                                                    <Form.Check className="mb-2" type="switch" id="expense_show_expense_details" label="Detalles del Gasto"
                                                                        checked={settings.expense_invoice_display_options?.show_expense_details !== false}
                                                                        onChange={(e) => setSettings({ ...settings, expense_invoice_display_options: { ...settings.expense_invoice_display_options, show_expense_details: e.target.checked } })}
                                                                    />
                                                                    <Form.Check className="mb-2" type="switch" id="expense_show_payment_method" label="Método de Pago"
                                                                        checked={settings.expense_invoice_display_options?.show_payment_method !== false}
                                                                        onChange={(e) => setSettings({ ...settings, expense_invoice_display_options: { ...settings.expense_invoice_display_options, show_payment_method: e.target.checked } })}
                                                                    />
                                                                    <Form.Check className="mb-0" type="switch" id="expense_show_qr_code" label="Mostrar Código QR"
                                                                        checked={settings.expense_invoice_display_options?.show_qr_code !== false}
                                                                        onChange={(e) => setSettings({ ...settings, expense_invoice_display_options: { ...settings.expense_invoice_display_options, show_qr_code: e.target.checked } })}
                                                                    />
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="d-flex flex-column align-items-center">
                                                                <h5 className="mb-3 text-muted">Vista Previa del Recibo de Gastos</h5>
                                                                <div className="border p-3 bg-light w-100 d-flex justify-content-center" style={{ minHeight: '500px', borderRadius: '8px' }}>
                                                                    <div style={{ transform: 'scale(0.9)', transformOrigin: 'top center', width: '100%' }}>
                                                                        <ExpenseReceiptPreview
                                                                            expense={{
                                                                                expense_id: '001',
                                                                                logo_url: logoFile ? URL.createObjectURL(logoFile) : (settings.logo_url || ''),
                                                                                business_name: businessName || 'Nombre del Negocio',
                                                                                business_address: settings.address || 'Calle Principal #123',
                                                                                business_phone: `${settings.phone_country_code || ''} ${settings.phone || ''}`.trim() || '+57 300 123 4567',
                                                                                fiscal_id_type: settings.fiscal_id_type,
                                                                                fiscal_id: settings.fiscal_id,
                                                                                qr_code_url: qrCodeFile ? URL.createObjectURL(qrCodeFile) : (settings.qr_code_url || ''),
                                                                                invoice_color: settings.expense_invoice_color || '#FFFFFF',
                                                                                invoice_footer: settings.expense_invoice_footer || '¡Gracias por su servicio! 🤝',
                                                                                date: new Date().toISOString(),
                                                                                category: 'Servicios',
                                                                                description: 'Pago de servicios mensuales',
                                                                                amount: 120000,
                                                                                payment_method: 'Efectivo',
                                                                                supplier_name: 'Proveedor Ejemplo S.A.S',
                                                                                supplier_tax_id: '900.123.456-7',
                                                                                supplier_phone: '+57 301 234 5678'
                                                                            }}
                                                                            options={settings.expense_invoice_display_options}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Tab.Pane>

                                                {/* Tab: Recibo de Liquidaciones (conditional) */}
                                                {business?.plan_features?.supplier_settlements && (
                                                    <Tab.Pane eventKey="settlementReceipt">
                                                        <Row>
                                                            <Col md={6}>
                                                                <Alert variant="info" className="mb-4">
                                                                    <small>Configura la apariencia del recibo de liquidación que se genera en <code>/settlement-receipt/&lt;id&gt;</code></small>
                                                                </Alert>

                                                                {/* Color Picker for Settlement */}
                                                                <Form.Group className="mb-4" controlId="settlement_invoice_color">
                                                                    <Form.Label>Color de Tema para Recibo de Liquidaciones</Form.Label>
                                                                    <Form.Control
                                                                        type="color"
                                                                        name="settlement_invoice_color"
                                                                        value={settings.settlement_invoice_color || '#FFFFFF'}
                                                                        onChange={handleChange}
                                                                        title="Selecciona un color de fondo para el recibo de liquidación"
                                                                        list="presetColorsSettlement"
                                                                        className="w-100"
                                                                        style={{ height: '40px' }}
                                                                    />
                                                                    <datalist id="presetColorsSettlement">
                                                                        <option>#FFFFFF</option>
                                                                        <option>#F0F8FF</option>
                                                                        <option>#FAEBD7</option>
                                                                        <option>#F5F5DC</option>
                                                                        <option>#E0FFFF</option>
                                                                        <option>#FAFAD2</option>
                                                                        <option>#D3D3D3</option>
                                                                        <option>#FFB6C1</option>
                                                                        <option>#87CEFA</option>
                                                                        <option>#F5F5F5</option>
                                                                    </datalist>
                                                                </Form.Group>

                                                                {/* Footer Text for Settlement */}
                                                                <Form.Group className="mb-4" controlId="settlement_invoice_footer">
                                                                    <Form.Label>Texto de Pie de Página</Form.Label>
                                                                    <Form.Control
                                                                        as="textarea"
                                                                        rows={2}
                                                                        name="settlement_invoice_footer"
                                                                        value={settings.settlement_invoice_footer || ''}
                                                                        onChange={handleChange}
                                                                        placeholder="Ej: Gracias por su colaboración."
                                                                    />
                                                                </Form.Group>

                                                                {/* Visible Sections Toggles for Settlement */}
                                                                <Card className="mb-4 bg-light border-0">
                                                                    <Card.Body>
                                                                        <h6 className="card-title mb-3">Secciones Visibles del Recibo de Liquidación</h6>
                                                                        <Form.Check className="mb-2" type="switch" id="settlement_show_supplier_info" label="Información del Proveedor"
                                                                            checked={settings.settlement_invoice_display_options?.show_supplier_info !== false}
                                                                            onChange={(e) => setSettings({ ...settings, settlement_invoice_display_options: { ...settings.settlement_invoice_display_options, show_supplier_info: e.target.checked } })}
                                                                        />
                                                                        <Form.Check className="mb-2" type="switch" id="settlement_show_deductions" label="Resumen y Deducciones"
                                                                            checked={settings.settlement_invoice_display_options?.show_deductions !== false}
                                                                            onChange={(e) => setSettings({ ...settings, settlement_invoice_display_options: { ...settings.settlement_invoice_display_options, show_deductions: e.target.checked } })}
                                                                        />
                                                                        <Form.Check className="mb-2" type="switch" id="settlement_show_payment_info" label="Estado de Pago"
                                                                            checked={settings.settlement_invoice_display_options?.show_payment_info !== false}
                                                                            onChange={(e) => setSettings({ ...settings, settlement_invoice_display_options: { ...settings.settlement_invoice_display_options, show_payment_info: e.target.checked } })}
                                                                        />
                                                                        <Form.Check className="mb-2" type="switch" id="settlement_show_notes" label="Notas / Observaciones"
                                                                            checked={settings.settlement_invoice_display_options?.show_notes !== false}
                                                                            onChange={(e) => setSettings({ ...settings, settlement_invoice_display_options: { ...settings.settlement_invoice_display_options, show_notes: e.target.checked } })}
                                                                        />
                                                                        <Form.Check className="mb-0" type="switch" id="settlement_show_qr_code" label="Mostrar Código QR"
                                                                            checked={settings.settlement_invoice_display_options?.show_qr_code !== false}
                                                                            onChange={(e) => setSettings({ ...settings, settlement_invoice_display_options: { ...settings.settlement_invoice_display_options, show_qr_code: e.target.checked } })}
                                                                        />
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                            <Col md={6}>
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <h5 className="mb-3 text-muted">Vista Previa del Recibo de Liquidación</h5>
                                                                    <div className="border p-3 bg-light w-100 d-flex justify-content-center" style={{ minHeight: '550px', borderRadius: '8px' }}>
                                                                        <div style={{ transform: 'scale(0.85)', transformOrigin: 'top center', width: '100%' }}>
                                                                            <SettlementReceiptPreview
                                                                                settlement={{
                                                                                    settlement_id: '001',
                                                                                    logo_url: logoFile ? URL.createObjectURL(logoFile) : (settings.logo_url || ''),
                                                                                    business_name: businessName || 'Nombre del Negocio',
                                                                                    business_address: settings.address || 'Calle Principal #123',
                                                                                    business_phone: `${settings.phone_country_code || ''} ${settings.phone || ''}`.trim() || '+57 300 123 4567',
                                                                                    fiscal_id_type: settings.fiscal_id_type,
                                                                                    fiscal_id: settings.fiscal_id,
                                                                                    qr_code_url: qrCodeFile ? URL.createObjectURL(qrCodeFile) : (settings.qr_code_url || ''),
                                                                                    invoice_color: settings.settlement_invoice_color || '#FFFFFF',
                                                                                    invoice_footer: settings.settlement_invoice_footer || 'Gracias por su colaboración.',
                                                                                    supplier_name: 'Proveedor ABC S.A.S',
                                                                                    supplier_tax_id: '900.987.654-3',
                                                                                    period_start: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
                                                                                    period_end: new Date().toISOString(),
                                                                                    gross_amount: 500000,
                                                                                    total_deductions: 50000,
                                                                                    notes: 'Pago acordado para el día 20'
                                                                                }}
                                                                                options={settings.settlement_invoice_display_options}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Tab.Pane>
                                                )}
                                            </Tab.Content>
                                        </Tab.Container>
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* Sección 6: Estaciones de Caja */}
                                {hasPermission(PERMISSIONS.MANAGE_CASH_REGISTER_STATIONS) && (
                                    <Accordion.Item eventKey="stations">
                                        <Accordion.Header>{t('settings.cashRegisterStations', 'Estaciones de Caja')}</Accordion.Header>
                                        <Accordion.Body>
                                            <CashRegisterStationsManager />
                                        </Accordion.Body>
                                    </Accordion.Item>
                                )}

                                {/* Sección 6b: Categorías de Gasto */}
                                {canEdit && business?.plan_features?.expenses && (
                                    <Accordion.Item eventKey="expenseCategories">
                                        <Accordion.Header>{t('settings.expenseCategories', 'Categorías de Gasto')}</Accordion.Header>
                                        <Accordion.Body>
                                            <ExpenseCategoriesManager />
                                        </Accordion.Body>
                                    </Accordion.Item>
                                )}

                                {/* Sección 7: Redes Sociales */}
                                <Accordion.Item eventKey="5">
                                    <Accordion.Header>{t('businessSettingsPage.sections.socialMedia')}</Accordion.Header>
                                    <Accordion.Body>
                                        <Row>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('businessSettingsPage.facebook')}</Form.Label><Form.Control type="url" name="social_media.facebook" value={settings.social_media.facebook || ''} onChange={handleChange} placeholder="https://facebook.com/yourpage" /></Form.Group></Col>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('businessSettingsPage.instagram')}</Form.Label><Form.Control type="url" name="social_media.instagram" value={settings.social_media.instagram || ''} onChange={handleChange} placeholder="https://instagram.com/yourprofile" /></Form.Group></Col>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('businessSettingsPage.twitter')}</Form.Label><Form.Control type="url" name="social_media.twitter" value={settings.social_media.twitter || ''} onChange={handleChange} placeholder="https://twitter.com/yourprofile" /></Form.Group></Col>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('businessSettingsPage.linkedin')}</Form.Label><Form.Control type="url" name="social_media.linkedin" value={settings.social_media.linkedin || ''} onChange={handleChange} placeholder="https://linkedin.com/in/yourprofile" /></Form.Group></Col>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('businessSettingsPage.tiktok')}</Form.Label><Form.Control type="url" name="social_media.tiktok" value={settings.social_media.tiktok || ''} onChange={handleChange} placeholder="https://tiktok.com/@yourprofile" /></Form.Group></Col>
                                            <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('businessSettingsPage.whatsapp')}</Form.Label><Form.Control type="tel" name="social_media.whatsapp" value={settings.social_media.whatsapp || ''} onChange={handleChange} placeholder="+15551234567" /></Form.Group></Col>
                                        </Row>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            {canEdit && (
                                <Button variant="primary" type="submit" className="d-block w-100 mt-3" disabled={submitting}>
                                    {submitting ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> {t('businessSettingsPage.savingButton')}</> : "Guardar Cambios"}
                                </Button>
                            )}

                        </fieldset>
                    </Form>
                </Card.Body>
            </Card >
        </Container >
    );
};

export default BusinessSettingsPage;